import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { TextSizes } from '../../enums/typography-text-sizes';
import { cn } from '../../utils/cn';
import { textVariants } from '../../variants/typography-text-variants';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot='tooltip-provider'
      delayDuration={delayDuration}
      {...props}
    />
  );
}

const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof TooltipPrimitive.Content> & {
    interactive?: boolean;
  }
>(
  (
    { className, sideOffset = 4, interactive = false, children, ...props },
    ref
  ) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        onPointerDownOutside={interactive ? undefined : e => e.preventDefault()}
        className={cn(
          textVariants({ size: TextSizes.XS }),
          'z-[10000] max-w-[400px] max-h-[200px] overflow-y-auto rounded-md bg-primary border border-secondary shadow-lg px-3 py-1.5 text-secondary animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  sideOffset?: number;
  duration?: number;
  disabled?: boolean;
  align?: TooltipPrimitive.TooltipContentProps['align'];
  interactive?: boolean;
}

function Tooltip({
  content,
  children,
  side,
  align,
  className,
  sideOffset,
  duration = 250,
  disabled = false,
  interactive = false,
}: TooltipProps) {
  if (disabled) {
    return children;
  }

  if (!content) {
    return children;
  }

  return (
    <TooltipPrimitive.Root
      delayDuration={duration}
      disableHoverableContent={!interactive}
    >
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={className}
        interactive={interactive}
      >
        {content}
      </TooltipContent>
    </TooltipPrimitive.Root>
  );
}

const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipRoot = TooltipPrimitive.Root;

export {
  Tooltip,
  TooltipProvider,
  TooltipContent,
  TooltipTrigger,
  TooltipRoot,
};

export type { TooltipProps };
